import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const mLabel = (m) => `${MON[Number(m.slice(5)) - 1]} ${m.slice(2, 4)}`;
const num = (v) => Number(v || 0).toLocaleString("en-IN");

function Bars({ months, values, max }) {
  return (
    <div className="flex h-12 items-end gap-1">
      {months.map((m) => {
        const v = values[m] || 0;
        const h = max ? Math.max((v / max) * 100, v ? 6 : 2) : 2;
        return <div key={m} title={`${mLabel(m)}: ${num(v)}`} className={`w-5 rounded-t ${v ? "bg-teal-500" : "bg-slate-200"}`} style={{ height: `${h}%` }} />;
      })}
    </div>
  );
}

function Delta({ cur, prev }) {
  if (!prev && !cur) return <Minus className="h-3.5 w-3.5 text-slate-300" />;
  const up = cur >= prev;
  const pct = prev ? Math.round(((cur - prev) / prev) * 100) : 100;
  const Icon = up ? TrendingUp : TrendingDown;
  return <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? "text-emerald-600" : "text-red-600"}`}><Icon className="h-3.5 w-3.5" />{pct > 0 ? "+" : ""}{pct}%</span>;
}

export default function DprActivityTrend() {
  const { ctx } = useWorkspace();
  const pid = ctx.project?.id;
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/dpr/activity-trend", { params: pid ? { project_id: pid } : {} }).then((r) => setData(r.data)).catch(() => setData({ months: [], rows: [] }));
  }, [pid]);
  const months = data?.months || [];
  const max = Math.max(0, ...(data?.rows || []).flatMap((r) => months.map((m) => r.months[m] || 0)));
  const [prevM, curM] = months.slice(-2);
  return (
    <section className="mt-8 rounded-xl border border-slate-200 bg-white p-4" data-testid="dashboard-section-dpr-trend">
      <h2 className="font-display text-lg font-semibold text-slate-900">DPR Activity Trend</h2>
      <p className="mb-3 text-xs text-slate-500">Project-wise total activity count — last 6 months (present teams only, current month to date)</p>
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : !data.rows.length ? (
        <p className="py-4 text-center text-sm text-slate-400" data-testid="dpr-trend-empty">No projects in scope.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase text-slate-500">
                <th className="px-2 py-2 text-left">Project</th>
                <th className="px-2 py-2 text-left">Trend</th>
                {months.map((m) => <th key={m} className="whitespace-nowrap px-2 py-2 text-right">{mLabel(m)}</th>)}
                <th className="px-2 py-2 text-right text-teal-700">6M Total</th>
                <th className="px-2 py-2 text-right">vs Last Month</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.project_id} className="border-b border-slate-100 last:border-0" data-testid={`dpr-trend-row-${r.project_id}`}>
                  <td className="whitespace-nowrap px-2 py-2 font-medium text-slate-700">{r.project_name}</td>
                  <td className="px-2 py-2"><Bars months={months} values={r.months} max={max} /></td>
                  {months.map((m) => <td key={m} className="px-2 py-2 text-right tabular-nums text-slate-600">{num(r.months[m])}</td>)}
                  <td className="px-2 py-2 text-right font-bold tabular-nums text-teal-800">{num(r.total)}</td>
                  <td className="px-2 py-2 text-right"><Delta cur={r.months[curM] || 0} prev={r.months[prevM] || 0} /></td>
                </tr>
              ))}
              {data.rows.length > 1 && (
                <tr className="bg-slate-50 font-semibold" data-testid="dpr-trend-total">
                  <td className="px-2 py-2 text-xs text-slate-700">TOTAL</td><td />
                  {months.map((m) => <td key={m} className="px-2 py-2 text-right tabular-nums text-slate-700">{num(data.month_totals[m])}</td>)}
                  <td className="px-2 py-2 text-right tabular-nums text-teal-900">{num(data.rows.reduce((a, r) => a + r.total, 0))}</td><td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
